import { Injectable, Injector } from "@angular/core";
import type { IProgressEvent } from "@ankaadia/ankaadia-shared";
import { ErrorCode, getProgressErrorMessage, isAggregateProgressError, isStructuredProgressError } from "@ankaadia/ankaadia-shared";
import { ProgressErrorNotifierBase, translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import type { GraphQLFormattedError } from "graphql";
import { MessageService } from "../../features/message/message.service";
import { ApolloErrorHandlerService } from "../apollo/apollo-error-handler.service";

@Injectable({ providedIn: "root" })
export class ProgressErrorNotifierService extends ProgressErrorNotifierBase {
  constructor(
    private readonly messageService: MessageService,
    private readonly injector: Injector
  ) {
    super();
    ProgressErrorNotifierService.instance = this;
    ProgressErrorNotifierBase.instance = this;
  }

  notifyError(event: IProgressEvent): void {
    if (isAggregateProgressError(event)) {
      this.notifyAggregateError(event);
    } else if (isStructuredProgressError(event)) {
      this.notifyStructuredError(event);
    } else {
      this.messageService.add({
        severity: "error",
        summary: translateSafely("user.badInput.title"),
        detail: getProgressErrorMessage(event),
      });
    }
  }

  private notifyAggregateError(event: IProgressEvent): void {
    const syntheticInnerErrors: GraphQLFormattedError[] = (event.aggregateErrors ?? []).map((entry) => ({
      message: entry.errorMessageKey,
      extensions: {
        code: ErrorCode.BAD_USER_INPUT,
        properties: [{ key: "errorMessageKey", value: entry.errorMessageKey }, ...(entry.errorProperties ?? [])],
      },
    }));

    const syntheticError: GraphQLFormattedError = {
      message: event.progressMessage ?? "Multiple errors occured.",
      extensions: {
        code: ErrorCode.AGGREGATEERROR,
        errors: syntheticInnerErrors,
      },
    };

    const handler = this.injector.get(ApolloErrorHandlerService);
    handler.handle({
      graphQLErrors: [syntheticError],
      operation: { operationName: "Subscription" } as any,
      forward: null as any,
    });
  }

  private notifyStructuredError(event: IProgressEvent): void {
    const syntheticError: GraphQLFormattedError = {
      message: event.errorMessageKey ?? event.progressMessage ?? "Unknown error",
      extensions: {
        code: event.errorCode ?? ErrorCode.BAD_USER_INPUT,
        properties: [
          ...(event.errorMessageKey ? [{ key: "errorMessageKey", value: event.errorMessageKey }] : []),
          ...(event.errorProperties ?? []),
        ],
      },
    };

    const handler = this.injector.get(ApolloErrorHandlerService);
    handler.handle({
      graphQLErrors: [syntheticError],
      operation: { operationName: "Subscription" } as any,
      forward: null as any,
    });
  }
}
